<script setup lang="ts">
/**
 * Строка списка задач — макет 12:342: чекбокс, тип, ключ, название, статус,
 * приоритет, исполнитель и срок. Состояния строки — макет 70:139.
 *
 * Статус и исполнитель меняются прямо здесь: список — то место, где размечают
 * несколько задач подряд, и ради каждой открывать карточку значит потерять
 * очередь. Остальное правят в панели задачи, которую раскрывает клик по строке
 * (DEV-1474), или на её странице — по ключу.
 *
 * Колонки «Пайплайн» в строке нет: `ListIssues` о пайплайнах не знает, а
 * запрашивать их по задаче — это пятнадцать запросов на страницу. Приедет
 * вместе с пакетной ручкой (DEV-1475), место под неё в сетке уже есть.
 */
import { AppAvatar } from '@vinprop/design-system'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import InlinePicker from '@/components/InlinePicker.vue'
import type { ProjectColumn, ProjectMember } from '@/features/projects/api'

import { formatDueDate } from '../format'
import IssueTypeIcon from './IssueTypeIcon.vue'
import PriorityGlyph from './PriorityGlyph.vue'
import StatusBadge from './StatusBadge.vue'
import SubtaskCount from './SubtaskCount.vue'

import type { IssueRow } from '../api'

const props = defineProps<{
  issue: IssueRow
  columns: ProjectColumn[]
  members: ProjectMember[]
  isSelected: boolean
}>()

const emit = defineEmits<{
  toggle: [id: string]
  move: [id: string, columnId: string]
  assign: [id: string, assigneeId: string, assigneeName: string]
}>()

const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()

const column = computed(() => props.columns.find((entry) => entry.id === props.issue.columnId))

const due = computed(() => formatDueDate(props.issue.dueDate, locale.value))

/**
 * Просроченный срок красный — но только у незакрытой задачи: у сделанной
 * прошлая дата это не долг, а история, и красить её значит звать чинить то,
 * что уже сделано.
 */
const isOverdue = computed(() => {
  if (!props.issue.dueDate) return false
  if (column.value?.statusCategory === 'STATUS_CATEGORY_DONE') return false

  return new Date(props.issue.dueDate).getTime() < Date.now()
})

const statusOptions = computed(() =>
  props.columns.map((entry) => ({ value: entry.id, label: entry.name })),
)

/** Снять исполнителя — такой же выбор, как назначить: пустое значение сверху */
const assigneeOptions = computed(() => [
  { value: '', label: t('issues.list.noAssignee') },
  ...props.members.filter((member) => member.name).map((m) => ({ value: m.id, label: m.name })),
])

const onAssign = (assigneeId: string) => {
  const member = props.members.find((entry) => entry.id === assigneeId)

  emit('assign', props.issue.id, assigneeId, member?.name ?? '')
}

/**
 * Клик по строке раскрывает панель задачи — кроме кликов по тому, что здесь
 * работает само: чекбоксу, пикерам статуса и исполнителя и ключу, который
 * уводит на страницу целиком.
 */
const onOpen = (event: MouseEvent) => {
  if ((event.target as HTMLElement).closest('a, button, input')) return

  void router.push({ query: { ...route.query, issue: props.issue.key } })
}
</script>

<template>
  <div
    class="irow"
    :class="{ 'irow--selected': isSelected }"
    role="button"
    tabindex="0"
    :aria-label="t('issues.list.openIssue', { key: issue.key })"
    @click="onOpen"
    @keydown.enter="router.push({ query: { ...route.query, issue: issue.key } })"
  >
    <input
      class="irow__check"
      type="checkbox"
      :checked="isSelected"
      :aria-label="t('issues.list.select', { key: issue.key })"
      @change="emit('toggle', issue.id)"
    />

    <IssueTypeIcon :type="issue.type" />

    <!-- Ключ — постоянный адрес задачи: по нему уходят на страницу целиком,
         а название раскрывает панель, не уводя из списка. -->
    <RouterLink class="irow__key" :to="`/issue/${issue.key}`">{{ issue.key }}</RouterLink>

    <!-- Счётчик подзадач живёт в ячейке названия: своей колонки под него в
         сетке нет, а рядом с названием он и читается — «что это за задача и
         сколько в ней осталось» — одним взглядом. -->
    <span class="irow__name">
      <span class="irow__title">{{ issue.title }}</span>
      <SubtaskCount :total="issue.subtasks.total" :done="issue.subtasks.done" />
    </span>

    <InlinePicker
      :options="statusOptions"
      :model-value="issue.columnId"
      :empty-text="t('toolbar.filters.empty')"
      @update:model-value="emit('move', issue.id, $event)"
    >
      <template #trigger="{ toggle }">
        <button type="button" class="irow__inline" :title="t('issues.list.changeStatus')" @click="toggle()">
          <StatusBadge :name="column?.name ?? '—'" :color="column?.color ?? ''" />
        </button>
      </template>
    </InlinePicker>

    <PriorityGlyph :priority="issue.priority" />

    <InlinePicker
      align="right"
      :options="assigneeOptions"
      :model-value="issue.assigneeId"
      :empty-text="t('issues.create.noMembers')"
      @update:model-value="onAssign"
    >
      <template #trigger="{ toggle }">
        <button
          type="button"
          class="irow__inline"
          :title="t('issues.list.changeAssignee')"
          @click="toggle()"
        >
          <AppAvatar :name="issue.assigneeName" :size="24" />
        </button>
      </template>
    </InlinePicker>

    <span class="irow__due" :class="{ 'irow__due--overdue': isOverdue }">{{ due || '—' }}</span>
  </div>
</template>

<style scoped>
.irow {
  display: grid;
  grid-template-columns: var(--itable-columns);
  align-items: center;
  gap: 12px;
  /* Строка — ребёнок колоночного флекса, и без этого она сплющивается под
     доступную высоту: на длинной странице от сорока пикселей остаётся
     двадцать восемь, и таблица едет вся разом. */
  flex-shrink: 0;
  height: 40px;
  padding: 0 16px;
  border-top: 1px solid var(--color-border);
}

.irow:hover {
  background: var(--color-bg-muted);
}

/* Двойной класс не для красоты: у `:hover` специфичность выше, и отмеченная
   строка под курсором иначе выглядит как обычная — ровно в тот момент, когда
   на неё смотрят. */
.irow.irow--selected {
  background: var(--color-primary-tint);
}

.irow__check {
  width: 16px;
  height: 16px;
  /* Свой браузерный margin у флажка сдвигает всю колонку — см. шапку таблицы */
  margin: 0;
  accent-color: var(--color-primary);
  cursor: pointer;
}

.irow__key {
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-mono);
  text-decoration: none;
  /* Строка моноширинного текста выше своего кегля, и без явной высоты она
     растягивает ячейку — соседние колонки от этого разъезжаются по вертикали. */
  line-height: 16px;
}

.irow__name {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.irow__title {
  overflow: hidden;
  color: var(--color-text-primary);
  font-size: var(--font-size-body-sm);
  line-height: 17px;
  text-decoration: none;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.irow__key:hover {
  color: var(--color-primary);
  text-decoration: underline;
}

.irow__title:hover {
  text-decoration: underline;
}

/* Триггеры инлайн-правки — не кнопки на вид: в макете это сама пилюля и сам
   аватар, кнопочная рамка вокруг них превратила бы строку в панель. */
.irow__inline {
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: none;
  border-radius: var(--radius-full);
  background: none;
  cursor: pointer;
}

.irow__inline:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
}

.irow__due {
  color: var(--color-text-secondary);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-mono);
  font-variant-numeric: tabular-nums;
  line-height: 16px;
}

.irow__due--overdue {
  color: var(--color-danger);
}
</style>
